import { lazy, Suspense } from 'react';
import { Link, Route, Routes } from 'react-router-dom';

const FacilitatorAccess = lazy(() =>
  import('./features/access/FacilitatorAccess').then((module) => ({
    default: module.FacilitatorAccess,
  })),
);
const JoinSession = lazy(() =>
  import('./features/access/JoinSession').then((module) => ({
    default: module.JoinSession,
  })),
);
const SessionEntry = lazy(() =>
  import('./features/access/SessionEntry').then((module) => ({
    default: module.SessionEntry,
  })),
);
const PresentationAccess = lazy(() =>
  import('./features/access/PresentationAccess').then((module) => ({
    default: module.PresentationAccess,
  })),
);
const AdminMode = lazy(() =>
  import('./features/admin/AdminMode').then((module) => ({
    default: module.AdminMode,
  })),
);

const commitments = [
  'Accessible structured table',
  'Facilitator-led guardrails',
  'Public synchronized play',
  'No participant accounts',
] as const;

function Header() {
  return (
    <header className="site-header">
      <Link className="brand" to="/" aria-label="Common Ground home">
        <span className="brand-mark" aria-hidden="true">
          CG
        </span>
        <span>
          <strong>Common Ground</strong>
          <small>Digital Table</small>
        </span>
      </Link>
      <span className="build-badge">V2 playtest table</span>
    </header>
  );
}

function Home() {
  return (
    <section className="hero" aria-labelledby="hero-title">
      <div className="hero-copy">
        <p className="eyebrow">Remote playtesting, rooted in the table</p>
        <h1 id="hero-title">Build capacity together.</h1>
        <p className="lede">
          A facilitator-led digital table where Farmers and Agents can see the
          whole system, move shared resources, and make decisions in
          conversation.
        </p>
        <div className="actions" aria-label="Session entry options">
          <Link className="button-link primary" to="/join">
            Join a session
          </Link>
          <Link className="button-link secondary" to="/facilitator">
            Facilitator sign in
          </Link>
        </div>
        <p className="foundation-note" role="status">
          Participants use only a display name and temporary session code.
        </p>
      </div>
      <aside className="principles-card" aria-labelledby="principles-title">
        <p className="card-kicker">V2 table commitments</p>
        <h2 id="principles-title">The room stays human.</h2>
        <ul>
          {commitments.map((item) => (
            <li key={item}>
              <span aria-hidden="true">✓</span>
              {item}
            </li>
          ))}
        </ul>
      </aside>
    </section>
  );
}

export function App() {
  return (
    <div className="app-shell">
      <Header />
      <main id="main-content">
        <Suspense
          fallback={
            <p className="route-loading" role="status">
              Opening the table…
            </p>
          }
        >
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/join" element={<JoinSession />} />
            <Route path="/facilitator" element={<FacilitatorAccess />} />
            <Route path="/admin" element={<AdminMode />} />
            <Route path="/session/:sessionId" element={<SessionEntry />} />
            <Route
              path="/presentation/:sessionId"
              element={<PresentationAccess />}
            />
            <Route path="*" element={<Home />} />
          </Routes>
        </Suspense>
      </main>
      <footer>
        <p>Common Ground V2 · Eight-Farm remote playtest prototype</p>
      </footer>
    </div>
  );
}
